import {
    Badge,
    Body1,
    Caption1,
    Input,
    Link,
    Spinner,
    Text,
    Title3,
    makeStyles,
    tokens,
} from '@fluentui/react-components';
import { Search24Regular } from '@fluentui/react-icons';
import React from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';

import { AppState } from '../../AppState';
import { useAppSelector } from '../../redux/app/hooks';
import { RootState } from '../../redux/app/store';
import { AttachmentProvider } from '../../contexts/AttachmentContext';
import { useChat } from '../../libs/hooks/useChat';
import { ChatRoomHeader } from '../chat/ChatRoomHeader';
import { ChatRoomInlineHeader } from '../chat/ChatRoomInlineHeader';
import { ChatSidebar } from '../chat/ChatSidebar';
import { ThirdPartyComponent } from '../../libs/models/ThirdPartyLicence';

const useClasses = makeStyles({
    root: {
        display: 'flex',
        flexDirection: 'column',
        width: '100%',
        height: '100%',
        border: 'none',
        backgroundColor: tokens.colorNeutralBackground3,
        boxShadow: 'var(--app-shadow-card)',
    },
    mainContainer: {
        display: 'flex',
        flexDirection: 'row',
        width: '100%',
        height: '100%',
        overflow: 'hidden',
    },
    content: {
        display: 'flex',
        flexDirection: 'column',
        flex: 1,
        height: '100%',
        overflow: 'hidden',
        maxWidth: '1200px',
        margin: '0 auto',
        width: '100%',
    },
    header: {
        display: 'flex',
        flexDirection: 'column',
        gap: tokens.spacingVerticalS,
        padding: tokens.spacingVerticalL,
        width: '100%',
        boxSizing: 'border-box',
    },
    pageTitle: {
        fontFamily: tokens.fontFamilyBase,
        fontWeight: tokens.fontWeightSemibold,
        fontSize: '20px',
        lineHeight: '100%',
        marginTop: tokens.spacingVerticalS,
        paddingBottom: tokens.spacingVerticalS,
        borderBottom: `1px solid ${tokens.colorNeutralStroke1}`,
    },
    intro: {
        color: tokens.colorNeutralForeground2,
    },
    search: {
        maxWidth: '360px',
    },
    // The list scrolls independently so the header/search stay pinned on long dependency trees.
    list: {
        flex: 1,
        overflowY: 'auto',
        padding: `0 ${tokens.spacingHorizontalL} ${tokens.spacingVerticalL}`,
        display: 'flex',
        flexDirection: 'column',
        gap: tokens.spacingVerticalXS,
    },
    row: {
        display: 'flex',
        flexDirection: 'column',
        gap: '2px',
        padding: `${tokens.spacingVerticalS} 0`,
        borderBottom: `1px solid ${tokens.colorNeutralStroke2}`,
    },
    rowTop: {
        display: 'flex',
        alignItems: 'center',
        gap: tokens.spacingHorizontalS,
        flexWrap: 'wrap',
    },
    name: {
        fontWeight: tokens.fontWeightSemibold,
    },
    version: {
        color: tokens.colorNeutralForeground3,
    },
    copyright: {
        color: tokens.colorNeutralForeground3,
    },
    stateContainer: {
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'center',
        flex: 1,
        gap: tokens.spacingVerticalM,
        color: tokens.colorNeutralForeground2,
    },
});

type LoadState = { status: 'loading' } | { status: 'error' } | { status: 'ready'; components: ThirdPartyComponent[] };

// The component URL comes from third-party dependency metadata (untrusted). Only allow http(s) so a
// package with a hostile "javascript:"/"data:" repository URL can't turn the attribution link into
// an XSS sink; anything else falls back to rendering the name as plain text.
const isSafeHttpUrl = (url: string): boolean => {
    try {
        const { protocol } = new URL(url);
        return protocol === 'http:' || protocol === 'https:';
    } catch {
        return false;
    }
};

// The licence file is generated by scripts/generate-licences.js but is fetched at runtime, so its
// fields are untrusted. Coerce each to a string so a missing or non-string value can't later throw
// in the search filter (c.name.toLowerCase()) or when rendered — it just becomes an empty string.
const asString = (value: unknown): string => (typeof value === 'string' ? value : '');

export const LicencesView: React.FC = () => {
    const classes = useClasses();
    const { embeddedMode } = useAppSelector((state: RootState) => state.app);
    const hostContext = useAppSelector((state: RootState) => state.hostContext.hostContext);
    const chat = useChat();
    const navigate = useNavigate();
    const { t } = useTranslation();

    const [state, setState] = React.useState<LoadState>({ status: 'loading' });
    const [query, setQuery] = React.useState('');

    React.useEffect(() => {
        let cancelled = false;
        const url = `${process.env.PUBLIC_URL ?? ''}/third-party-licences.json`;
        fetch(url)
            .then((response) => {
                if (!response.ok) {
                    throw new Error(`Failed to load licences: ${String(response.status)}`);
                }
                // Untrusted payload — the file is fetched at runtime, so treat the top-level shape
                // and each component's fields as possibly-absent rather than trusting the type.
                return response.json() as Promise<{ components?: Array<Partial<ThirdPartyComponent>> }>;
            })
            .then((data) => {
                if (!cancelled) {
                    // Normalise every field at this trust boundary (see asString): a component with a
                    // missing or non-string name/licence would otherwise throw inside the search
                    // useMemo (c.name.toLowerCase()) and blank the whole view instead of degrading.
                    const components: ThirdPartyComponent[] = (data.components ?? []).map((c) => ({
                        name: asString(c.name),
                        version: asString(c.version),
                        licence: asString(c.licence),
                        url: asString(c.url),
                        publisher: asString(c.publisher),
                        copyright: asString(c.copyright),
                    }));
                    setState({ status: 'ready', components });
                }
            })
            .catch(() => {
                if (!cancelled) {
                    setState({ status: 'error' });
                }
            });
        return () => {
            cancelled = true;
        };
    }, []);

    // The shared header's "new chat" affordance creates a chat and returns to the chat view, same as
    // SettingsView/HistoryPage. The navigate('/') is required: without it a new chat is created in
    // Redux but the router stays on /licences, leaving the user stranded on the licence list.
    const handleNewChat = async () => {
        try {
            await chat.createChat(hostContext);
            await navigate('/');
        } catch (error) {
            console.error('Failed to create new chat:', error);
        }
    };

    const setAppState: React.Dispatch<React.SetStateAction<AppState>> = (newState) => {
        if (newState === AppState.SettingUserInfo) {
            void handleNewChat();
        }
    };

    const filtered = React.useMemo(() => {
        if (state.status !== 'ready') {
            return [];
        }
        const needle = query.trim().toLowerCase();
        if (!needle) {
            return state.components;
        }
        return state.components.filter(
            (c) => c.name.toLowerCase().includes(needle) || c.licence.toLowerCase().includes(needle),
        );
    }, [state, query]);

    return (
        <AttachmentProvider>
            <div className={classes.root}>
                {!embeddedMode && <ChatRoomHeader setAppState={setAppState} />}
                <ChatRoomInlineHeader setAppState={setAppState} isVisible={embeddedMode} />
                <div className={classes.mainContainer}>
                    <ChatSidebar />
                    <div className={classes.content} data-testid="licencesView">
                        <div className={classes.header}>
                            <Title3 className={classes.pageTitle}>{t('licences.title')}</Title3>
                            <Body1 className={classes.intro}>{t('licences.intro')}</Body1>
                            {state.status === 'ready' && (
                                <>
                                    <Input
                                        className={classes.search}
                                        value={query}
                                        onChange={(_, data) => {
                                            setQuery(data.value);
                                        }}
                                        contentBefore={<Search24Regular />}
                                        placeholder={t('licences.searchPlaceholder')}
                                        data-testid="licencesSearch"
                                    />
                                    <Caption1>{t('licences.count', { n: filtered.length })}</Caption1>
                                </>
                            )}
                        </div>

                        {state.status === 'loading' && (
                            <div className={classes.stateContainer} data-testid="licencesLoading">
                                <Spinner label={t('licences.loading')} />
                            </div>
                        )}

                        {state.status === 'error' && (
                            <div className={classes.stateContainer} data-testid="licencesError">
                                <Text>{t('licences.error')}</Text>
                            </div>
                        )}

                        {state.status === 'ready' && (
                            <div className={classes.list} data-testid="licencesList">
                                {filtered.length === 0 ? (
                                    <div className={classes.stateContainer}>
                                        <Text>{t('licences.empty')}</Text>
                                    </div>
                                ) : (
                                    filtered.map((c) => (
                                        <div className={classes.row} key={`${c.name}@${c.version}`}>
                                            <div className={classes.rowTop}>
                                                <Text className={classes.name}>
                                                    {c.url && isSafeHttpUrl(c.url) ? (
                                                        <Link href={c.url} target="_blank" rel="noopener noreferrer">
                                                            {c.name}
                                                        </Link>
                                                    ) : (
                                                        c.name
                                                    )}
                                                </Text>
                                                <Caption1 className={classes.version}>{c.version}</Caption1>
                                                <Badge appearance="outline" color="informative">
                                                    {c.licence}
                                                </Badge>
                                            </div>
                                            {c.copyright && (
                                                <Caption1 className={classes.copyright}>{c.copyright}</Caption1>
                                            )}
                                        </div>
                                    ))
                                )}
                            </div>
                        )}
                    </div>
                </div>
            </div>
        </AttachmentProvider>
    );
};
